import _ from "lodash";
import React from "react";
import MicrosoftPickerChooser from "./MicrosoftPickerChooser";
import PropTypes from "prop-types";
import { compose } from "lib/apolloClient";
import { OneDriveIcon } from "SvgComponents";
import I18nHOC from "UIComponents/I18nHOC";
import { PICKER_DIRECT_DOWNLOADABLE_TYPES, colors } from "Constants";
import {
  generateRandomId,
  getFileType,
  getFileName,
  getImageDimensions,
  getMicrosoftFileDetails,
  getMaxSize,
  getMaxSizeForVideoFiles,
  getMaxSizeForZipFiles,
  getUpdatedMaxSize,
  getFileTypesAndMaxSizes,
} from "Utils";
import {
  uploadFile,
  setProgress,
  downloadFromUrl,
  removeProgressElement,
  exceededSizeError,
  heicToJpg,
} from "modules/Services";
import { connect } from "react-redux";
import DriveShareModal from "../DriveShareModal";
import { getCurriculumProgramFreeStatus } from "Platform/modules/PlatformModule";
import ACLStore from "lib/aclStore";
import Drive from "./Drive";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { Sentry } from "services/sentry";
import { ARCHIVE_MIME_TYPES } from "Utils/AttachmentHelpers";
import { is500MBVideoUploadLimitEnabled } from "Post/utils";

const START_PROGRESS = 50;

class MicrosoftPicker extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      accessToken: "",
      files: [],
      showShareModal: false,
      showDriveModal: false,
    };
  }

  updateAccessToken = ({ accessToken }) => {
    this.setState({ accessToken, showDriveModal: true });
  };

  updateFileCount(count, isError = false) {
    const { updateFilesCount } = this.props;

    if (updateFilesCount) {
      updateFilesCount(count, isError);
    }
  }

  getSendObject = async ({ file, item, isLink }) => {
    const mimeType = _.get(file, "mimeType")
      ? file.mimeType
      : _.get(file, "type");
    const url = isLink ? file.url : item;
    // const url = isLink ? file.url : file.downloadUrl;
    const attachmentType =
      this.shouldExportAsLink(mimeType) || isLink
        ? "LINK"
        : getFileType(mimeType).text;
    const dimensions = await getImageDimensions({ url, type: attachmentType });

    const metadata = {
      size: _.get(file, "size", 0),
      serviceType: "MICROSOFT",
      fileType:
        _.get(file, "type") ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          ? "document"
          : _.get(file, "type"),
      fileId: _.get(file, "id"),
    };

    if (!_.isEqual({ height: 520, width: 520 }, dimensions)) {
      metadata.height = dimensions.height;
      metadata.width = dimensions.width;
    }
    // console.log("metadata", metadata);
    if (file.permission) {
      metadata.permission = file.permission;
    }
    return {
      type: attachmentType,
      url: url,
      name: getFileName(file.name),
      mimeType: mimeType,
      metadata,
    };
  };

  onFileDownloadedFromDrive = async ({ file, uploadId, startProgress }) => {
    const {
      onContentLoaded,
      onFailedAllowedMimeType,
      shouldUpload,
      parentType,
      parentId,
      portalType,
      uploadFile,
      onContentUploaded,
      onSingleContentUploaded,
      removeProgressElement,
      heicToJpg,
      setToastMsg,
    } = this.props;
    let updatedFile = file;
    if (
      ACLStore.can("FeatureFlag:EnableHEICConversion") &&
      _.includes(["image/heic", "image/heif"], _.get(file, "type"))
    ) {
      try {
        updatedFile = await heicToJpg({ file, uploadId, startProgress: 0 });
      } catch (e) {
        Sentry.captureMessage("HEIC_CONVERSION_ISSUE_", {
          extra: {
            functionName: "heicToJpg",
            reason: _.get(e, "message"),
            fileType: _.get(file, "type"),
            component: "MicrosoftPicker",
          },
        });
      }
    }
    if (onContentLoaded) {
      // Handle your own upload

      const url = URL.createObjectURL(updatedFile);
      const sendObj = await this.getSendObject({
        file: updatedFile,
        item: url,
      });
      onContentLoaded({ ...sendObj, url, file: updatedFile });
      removeProgressElement({ id: uploadId });
      this.updateFileCount(-1);
    } else {
      if (shouldUpload) {
        // Uploading asset to our server. only when file has allowed mimetype

        return new Promise((resolve, reject) => {
          this.getSendObject({
            file: updatedFile,
          })
            .then(res => {
              let sendObj = res;
              const uploadStartTime = Date.now();

              const { promise } = uploadFile({
                file: updatedFile,
                uploadId,
                startProgress,
                parentType,
                parentId,
                attachment: sendObj,
                portalType,
              });

              promise
                .then(async item => {
                  sendObj = await this.getSendObject({
                    file: updatedFile,
                    item,
                  });
                  const uploadTime = Date.now() - uploadStartTime;
                  const metadata = _.get(sendObj, "metadata", {});
                  const updatedMetadata = { ...metadata, uploadTime };
                  sendObj = { ...sendObj, metadata: updatedMetadata };
                  if (onContentUploaded) {
                    await onContentUploaded(sendObj);
                  }
                  if (onSingleContentUploaded) {
                    await onSingleContentUploaded(sendObj, uploadId, {
                      parentId,
                      parentType,
                    });
                  }
                  removeProgressElement({ id: uploadId });
                  this.updateFileCount(-1);
                  resolve(sendObj);
                })
                .catch(e => {
                  //console.log(e);
                  this.updateFileCount(-1, true);
                  reject(e);
                });
            })
            .catch(reject);
        });
      }
    }
  };

  shouldExportAsLink = mimeType => {
    const { exportAsPdf, enableShareModal } = this.props;

    if (enableShareModal) {
      return false;
    }

    if (_.includes(PICKER_DIRECT_DOWNLOADABLE_TYPES, mimeType)) {
      return false;
    }

    return !exportAsPdf;
  };

  chooseFiles = async data => {
    if (_.isEmpty(data)) {
      // console.log("data is empty");
      this.onClose();
      return;
    }
    const { enableShareModal } = this.props;

    if (enableShareModal) {
      this.setState({
        showShareModal: true,
        files: data,
        showDriveModal: false,
      });
    } else {
      this.processChosenFiles({ files: data, showDriveModal: false });
    }
  };

  handleOnMultipleFileUpload = async ({ sendObjList, files }) => {
    const { onMultipleFilesUploaded } = this.props;

    if (_.size(sendObjList) == _.size(files) && onMultipleFilesUploaded) {
      const values = _.filter(await Promise.all(sendObjList), urlObj => urlObj);
      onMultipleFilesUploaded(values);
    }
  };

  customizedFile = async ({ file, uploadId }) => {
    let customFile;
    if (this.props.customizedFile) {
      file.preview = URL.createObjectURL(file);
      this.props.removeProgressElement({ id: uploadId });
      customFile = await this.props.customizedFile(file);
    } else {
      return file;
    }
    return customFile;
  };

  processChosenFiles = ({ files }) => {
    const {
      onContentLoaded,
      onDropContent,
      downloadFromUrl,
      parentType,
      parentId,
      enableShareModal,
      t,
      maxSize,
      maxSizeForVideoFiles,
      maxSizeForZipFiles,
      isIncreasedUploadLimit,
    } = this.props;
    const { accessToken } = this.state;

    try {
      //picker type is onedrive drive
      const sendObjList = [];

      //checking file size limit
      let allowedFilesWithSizeLimit = _.filter(files, file => {
        const fileType = file?.mimeType;

        const isLink = enableShareModal
          ? _.isEqual(file.exportAs, "LINK")
          : this.shouldExportAsLink(file.mimeType);
        if (!isLink) {
          const updatedMaxSize = getUpdatedMaxSize({
            maxSize: _.isArray(maxSize)
              ? maxSize
              : _.max(maxSize, maxSizeForVideoFiles, maxSizeForZipFiles),
            fileType,
          });

          return _.isNumber(updatedMaxSize)
            ? updatedMaxSize >= _.get(file, "size", 0)
            : true;
        }
        return true;
      });

      if (!_.isArray(maxSize)) {
        const oversizedFiles = _.filter(allowedFilesWithSizeLimit, file => {
          const isLink = enableShareModal
            ? _.isEqual(file.exportAs, "LINK")
            : this.shouldExportAsLink(file.mimeType);
          if (isLink) {
            return false;
          }
          let maxSizeForFile = maxSize;
          if (
            is500MBVideoUploadLimitEnabled() &&
            file?.mimeType?.startsWith("video/")
          ) {
            maxSizeForFile = maxSizeForVideoFiles;
          }

          if (
            isIncreasedUploadLimit &&
            _.includes(ARCHIVE_MIME_TYPES, file?.mimeType)
          ) {
            maxSizeForFile = maxSizeForZipFiles;
          }

          return maxSizeForFile < _.get(file, "size", 0);
        });

        if (!_.isEmpty(oversizedFiles)) {
          allowedFilesWithSizeLimit = _.filter(
            allowedFilesWithSizeLimit,
            file => {
              return !_.some(
                oversizedFiles,
                oversizedFile =>
                  file.name === oversizedFile.name &&
                  file.size === oversizedFile.size &&
                  file.lastModified === oversizedFile.lastModified
              );
            }
          );
        }
      }

      allowedFilesWithSizeLimit.map(async (file, key) => {
        // console.log("file", file);
        this.updateFileCount(1);

        const fileUploadId = generateRandomId();
        const fileDetails = await getMicrosoftFileDetails({
          fileId: file.id,
          accessToken,
        });

        const thumbUrl = _.get(fileDetails, "thumbnailLink");

        const isLink = enableShareModal
          ? _.isEqual(file.exportAs, "LINK")
          : this.shouldExportAsLink(file.mimeType);
        const sendObj = await this.getSendObject({
          file,
          item: thumbUrl,
          isLink,
        });
        //Sending this metadata to for BE to download and upload to S3.
        //Sending this with object as thumbUrl with to show preview while creation.
        if (thumbUrl) {
          sendObj.metadata.tempThumbUrl = thumbUrl;
          sendObj.thumbUrl = thumbUrl;
        }
        if (onDropContent) {
          // Here we have either thumb-url or link url(actual file url) as file url
          onDropContent({ ...sendObj, id: fileUploadId });
        }
        // let startProgress = 50;
        if (!isLink) {
          // File is not of type link, i.e may be exportAsPdf is true or it is inside the allowed mimetype
          const fileUrl = _.get(file, "downloadUrl") || _.get(file, "url");
          // : await getExportableFileUrl({
          //     file,
          //     accessToken,
          //   });

          // Starting the download of file from drive.
          const headers = !ACLStore.can(
            "FeatureFlag:DisableMicrosoftPickerDownloadFileHeaders"
          )
            ? [{ label: "Authorization", value: `Bearer ${accessToken}` }]
            : [];
          downloadFromUrl({
            downloadUrl: fileUrl,
            size: file.size,
            name: file.name,
            parentType: parentType,
            parentId: parentId,
            attachment: sendObj,
            headers,
            fileUploadId: fileUploadId,
            showErrorMessage: true,
            onContentLoadFailed: async ({ isCancelled }) => {
              if (!isCancelled && !enableShareModal) {
                const sendObjVal = await this.getSendObject({
                  file,
                  item: thumbUrl,
                  isLink: true,
                });
                this.onContentLoaded({ sendObj: sendObjVal, fileUploadId });
              } else {
                this.updateFileCount(-1, true);
              }
            },
            onLoaded: async fileBlob => {
              if (fileBlob) {
                fileBlob.name = _.get(file, "name", "Untitled");
                if (!fileBlob.type) {
                  fileBlob.mimeType = _.get(file, "mimeType");
                }
              }

              fileBlob = await this.customizedFile({
                file: fileBlob,
                uploadId: fileUploadId,
              });
              const sendObj = this.onFileDownloadedFromDrive({
                file: fileBlob,
                uploadId: fileUploadId,
                startProgress: START_PROGRESS,
              });
              sendObjList.push(sendObj);

              // Sends all files' sendObj together as an array
              this.handleOnMultipleFileUpload({
                sendObjList,
                files: allowedFilesWithSizeLimit,
              });
            },
            startProgress: START_PROGRESS,
          });
        } else {
          this.onContentLoaded({ sendObj, fileUploadId });
          sendObjList.push(sendObj);

          this.handleOnMultipleFileUpload({
            sendObjList,
            files: allowedFilesWithSizeLimit,
            // fileCount
          });
        }
      });

      if (_.size(files) > _.size(allowedFilesWithSizeLimit)) {
        const fileTypesAndMaxSizes = getFileTypesAndMaxSizes({
          files,
          allowedFiles: allowedFilesWithSizeLimit,
          fileTypePropName: "mimeType",
          maxSize: this.props.maxSize,
          maxSizeForVideoFiles: this.props.maxSizeForVideoFiles,
          maxSizeForZipFiles: this.props.maxSizeForZipFiles,
          is500MBVideoLimitEnabled: is500MBVideoUploadLimitEnabled(),
          isIncreasedUploadLimit: this.props.isIncreasedUploadLimit,
        });

        this.props.exceededSizeError({
          fileTypesAndMaxSizes,
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  onContentLoaded = ({ sendObj, fileUploadId }) => {
    const {
      parentId,
      parentType,
      onSingleContentUploaded,
      onContentUploaded,
      onContentLoaded,
    } = this.props;

    if (onContentLoaded) {
      onContentLoaded(sendObj);
    }
    // we have a link of file
    if (onContentUploaded) {
      onContentUploaded(sendObj);
    }
    if (onSingleContentUploaded) {
      onSingleContentUploaded(sendObj, fileUploadId, {
        parentId,
        parentType,
      });
    }
    this.updateFileCount(-1);
  };

  onClose = () => {
    this.setState({ showDriveModal: false });
    const { onClose } = this.props;

    if (onClose) {
      onClose();
    }
  };

  toggleSharingFormatModal = () => {
    const { showShareModal } = this.state;

    if (showShareModal) {
      this.onClose();
    }

    this.setState({ showShareModal: !showShareModal });
  };

  getMimeTypesArray = ({ accept }) => {
    if (!(_.isNull(accept) || _.isUndefined(accept)) && _.isString(accept)) {
      return _.split(accept, ",");
    }
    return accept || [];
  };

  render() {
    const {
      enableShareModal,
      renderComponent,
      classStyle,
      multiselect,
      sourceType,
      accept,
      setInnerPickerRef,
      defaultExportType,
      attachmentExportOptions,
      disabled,
      excludeFileExtensionsList,
      customFilterFilesHandler,
      shouldStopPropagation,
      userType,
    } = this.props;
    const { files, showShareModal, accessToken, showDriveModal } = this.state;
    const mimeTypes = this.getMimeTypesArray({ accept });

    return (
      <React.Fragment>
        <MicrosoftPickerChooser
          //   onChange={data => {
          //     this.chooseFiles(data);
          //   }}

          mimeTypes={mimeTypes}
          multiselect={multiselect}
          classStyle={classStyle}
          updateAccessToken={this.updateAccessToken}
          setInnerPickerRef={setInnerPickerRef}
          disabled={disabled}
          shouldStopPropagation={shouldStopPropagation}
        >
          {renderComponent ? (
            renderComponent
          ) : (
            <div className={classStyle}>
              <OneDriveIcon fill={colors.neutral400} />
            </div>
          )}
        </MicrosoftPickerChooser>

        {showDriveModal && !showShareModal && accessToken && (
          <div
            onClick={e => {
              e.stopPropagation();
            }}
          >
            <Drive
              multiselect={multiselect}
              onChoose={this.chooseFiles}
              accessToken={accessToken}
              accept={_.uniq(accept)}
              updateRef={null}
              onClose={this.onClose}
              excludeFileExtensionsList={excludeFileExtensionsList}
              customFilterFilesHandler={customFilterFilesHandler}
            />
          </div>
        )}
        {enableShareModal && showShareModal && (
          <div
            onClick={e => {
              e.stopPropagation();
            }}
          >
            <DriveShareModal
              serviceType={"MICROSOFT"}
              processChosenFiles={this.processChosenFiles}
              onClickClose={this.toggleSharingFormatModal}
              files={files}
              accessToken={accessToken}
              sourceType={sourceType}
              defaultExportType={defaultExportType}
              attachmentExportOptions={attachmentExportOptions}
              customStyle={{ zIndex: 1151 }}
              userType={userType}
            />
          </div>
        )}
      </React.Fragment>
    );
  }
}

const mapActionCreators = {
  uploadFile,
  setProgress,
  downloadFromUrl,
  removeProgressElement,
  exceededSizeError,
  heicToJpg,
  setToastMsg,
};

const mapStateToProps = (state, ownProps) => {
  let { maxSize } = ownProps;
  const { isIncreasedUploadLimit = false } = ownProps;
  const isCurriculumProgramFree = getCurriculumProgramFreeStatus({ state });
  const allowInfiniteFileSize = ACLStore.can(
    "FeatureFlag:DropzoneInfiniteFileSize"
  );

  const maxSizeForVideoFiles = getMaxSizeForVideoFiles({
    maxSize,
    isCurriculumProgramFree,
    is500MBVideoLimit: is500MBVideoUploadLimitEnabled(),
    allowInfiniteFileSize,
  });

  const maxSizeForZipFiles = getMaxSizeForZipFiles({
    maxSize,
    isCurriculumProgramFree,
    allowInfiniteFileSize,
    isIncreasedUploadLimit,
  });

  maxSize = getMaxSize({
    maxSize,
    isCurriculumProgramFree,
    allowInfiniteFileSize,
  });

  return {
    maxSize,
    maxSizeForVideoFiles,
    maxSizeForZipFiles,
    userType: state.login.userInfo.user_type,
    isCurriculumProgramFree,
  };
};

MicrosoftPicker.propTypes = {
  accept: PropTypes.array,
  enableShareModal: PropTypes.bool,
  onContentLoaded: PropTypes.func, //handle content upload on your own
  onContentUploaded: PropTypes.func,
  onContentUploadProgress: PropTypes.func,
  updateFilesCount: PropTypes.func,
  renderComponent: PropTypes.element,
  multiselect: PropTypes.bool,
  onClickEvent: PropTypes.func,
  onSingleContentUploaded: PropTypes.func,
  onDropContent: PropTypes.func,
  customizedFile: PropTypes.func,
  parentId: PropTypes.string,
  parentType: PropTypes.string,
  enableShareDrive: PropTypes.bool,
  exportAsPdf: PropTypes.bool,
  sourceType: PropTypes.oneOf(["RESOURCE", "STUDENT_TEMPLATE"]),
  shouldStopPropagation: PropTypes.bool,
};

MicrosoftPicker.defaultProps = {
  accept: [],
  shouldUpload: true,
  multiselect: true,
  parentType: "",
  parentId: null,
  enableShareDrive: true,
  exportAsPdf: false,
  enableShareModal: true,
  sourceType: "RESOURCE",
  shouldStopPropagation: true,
};

export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(mapStateToProps, mapActionCreators)
)(MicrosoftPicker);
